import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  ShoppingCart,
  Package,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Database,
  DollarSign,
  AlertTriangle,
  Zap,
  BarChart3,
  Globe,
  Store,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Users,
  Box,
  Truck,
  Calendar,
  ShoppingBag,
} from "lucide-react";

const API = "/api";

export default function DashboardPage() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoSyncStatus, setAutoSyncStatus] = useState(null);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [shopifyStats, setShopifyStats] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({
    nebim: { status: 'checking', message: 'Kontrol ediliyor...' },
    shopify: { status: 'checking', message: 'Kontrol ediliyor...' }
  });

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats?token=${token}`);
      setStats(response.data);
    } catch (error) {
      console.error("Stats fetch error:", error);
    }
  };

  const fetchAutoSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/auto-sync/status?token=${token}`);
      setAutoSyncStatus(response.data);
    } catch (error) {
      console.error("Auto sync status error:", error);
    }
  };

  const fetchLowStockProducts = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/low-stock?token=${token}`);
      setLowStockProducts(response.data.products || []);
    } catch (error) {
      console.error("Low stock fetch error:", error);
    }
  };

  const fetchConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/connection-status?token=${token}`);
      setConnectionStatus(response.data);
    } catch (error) {
      // Default durumları ayarla
      setConnectionStatus({
        nebim: { status: 'unknown', message: 'Bağlantı kontrol edilemedi' },
        shopify: { status: 'unknown', message: 'Bağlantı kontrol edilemedi' }
      });
    }
  };

  const fetchShopifyStats = async () => {
    try {
      const response = await axios.get(`${API}/shopify-orders/stats?token=${token}`);
      setShopifyStats(response.data);
    } catch (error) {
      console.error("Shopify stats error:", error);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchStats(),
      fetchAutoSyncStatus(),
      fetchLowStockProducts(),
      fetchConnectionStatus(),
      fetchShopifyStats()
    ]);
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAll();
    setRefreshing(false);
    toast.success("Dashboard güncellendi");
  };

  useEffect(() => {
    if (token) {
      fetchAll();
      // Her 30 saniyede bir güncelle
      const interval = setInterval(fetchAll, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const getLogIcon = (type) => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case "warning":
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      default:
        return <Clock className="w-4 h-4 text-blue-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'bg-emerald-500';
      case 'error': return 'bg-red-500';
      case 'checking': return 'bg-blue-500 animate-pulse';
      default: return 'bg-amber-500';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('tr-TR').format(num || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-3" />
          <p className="text-slate-500">Dashboard yükleniyor...</p>
        </div>
      </div>
    );
  }

  const syncPercentage = stats?.products?.total > 0 
    ? Math.round((stats?.products?.synced / stats?.products?.total) * 100) 
    : 0;

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Shopify-Nebim V3 Entegrasyon Merkezi</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
        </div>
      </div>

      {/* Ana Metrikler */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-tour="stats-cards">
        {/* Shopify Toplam Sipariş */}
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200" data-testid="stats-shopify-orders">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Shopify Sipariş</p>
                <p className="text-3xl font-bold text-green-900 mt-1">{formatNumber(shopifyStats?.total_orders)}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Badge variant="secondary" className="bg-green-200 text-green-800">
                    {shopifyStats?.synced_to_nebim || 0} aktarıldı
                  </Badge>
                </div>
              </div>
              <div className="p-3 bg-green-500 rounded-xl">
                <ShoppingBag className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Nebim'e Bekleyen */}
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200" data-testid="stats-pending-orders">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">Nebim Bekleyen</p>
                <p className="text-3xl font-bold text-amber-900 mt-1">{formatNumber(shopifyStats?.pending_nebim)}</p>
                <p className="text-xs text-amber-600 mt-2">Nebim'e aktarılmayı bekliyor</p>
              </div>
              <div className="p-3 bg-amber-500 rounded-xl">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Toplam Ürün */}
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200" data-testid="stats-total-products">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-600">Toplam Ürün</p>
                <p className="text-3xl font-bold text-emerald-900 mt-1">{formatNumber(stats?.products?.total)}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Progress value={syncPercentage} className="w-20 h-2" />
                  <span className="text-xs text-emerald-600">{syncPercentage}% sync</span>
                </div>
              </div>
              <div className="p-3 bg-emerald-500 rounded-xl">
                <Package className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mağaza Sayısı */}
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200" data-testid="stats-stores">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Mağaza Sayısı</p>
                <p className="text-3xl font-bold text-purple-900 mt-1">{shopifyStats?.store_stats?.length || 0}</p>
                <p className="text-xs text-purple-600 mt-2">Bağlı Shopify mağazası</p>
              </div>
              <div className="p-3 bg-purple-500 rounded-xl">
                <Store className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Shopify Sipariş Özeti */}
      <Card className="border-green-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-green-500" />
              Shopify Sipariş Özeti
            </CardTitle>
            <Button variant="outline" size="sm" asChild>
              <a href="/orders">Siparişlere Git</a>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-xl">
              <p className="text-3xl font-bold text-green-600">{shopifyStats?.total_orders || 0}</p>
              <p className="text-sm text-green-600 mt-1">Toplam Sipariş</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-xl">
              <p className="text-3xl font-bold text-blue-600">{shopifyStats?.synced_to_nebim || 0}</p>
              <p className="text-sm text-blue-600 mt-1">Nebim'e Aktarılan</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-xl">
              <p className="text-3xl font-bold text-amber-600">{shopifyStats?.pending_nebim || 0}</p>
              <p className="text-sm text-amber-600 mt-1">Bekleyen</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-xl">
              <p className="text-3xl font-bold text-purple-600">{shopifyStats?.store_stats?.length || 0}</p>
              <p className="text-sm text-purple-600 mt-1">Mağaza</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bağlantı Durumu + Düşük Stok Uyarıları */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bağlantı Durumları */}
        <Card data-tour="connection-status">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              Bağlantı Durumu
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* API */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
                <span className="font-medium text-slate-700">API Sunucusu</span>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">Aktif</Badge>
            </div>
            
            {/* Nebim */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor(connectionStatus?.nebim?.status)}`} />
                <span className="font-medium text-slate-700">Nebim V3</span>
              </div>
              <Badge className={connectionStatus?.nebim?.status === 'connected' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                {connectionStatus?.nebim?.status === 'connected' ? 'Bağlı' : 'Kontrol Et'}
              </Badge>
            </div>
            
            {/* Shopify */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor(connectionStatus?.shopify?.status)}`} />
                <span className="font-medium text-slate-700">Shopify</span>
              </div>
              <Badge className={connectionStatus?.shopify?.status === 'connected' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                {connectionStatus?.shopify?.status === 'connected' ? 'Bağlı' : 'Kontrol Et'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Düşük Stok Uyarıları */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Düşük Stok Uyarıları
              </CardTitle>
              {lowStockProducts.length > 0 && (
                <Badge variant="destructive">{lowStockProducts.length} Ürün</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {lowStockProducts.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {lowStockProducts.slice(0, 5).map((product, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex items-center gap-3">
                      <Box className="w-4 h-4 text-red-500" />
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{product.name || product.productCode}</p>
                        <p className="text-xs text-slate-500">{product.barcode}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-red-600">{product.stock} adet</p>
                      <p className="text-xs text-slate-500">Kalan stok</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-2" />
                <p className="text-slate-500">Tüm stoklar yeterli seviyede</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Hızlı İşlemler + Son Loglar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hızlı İşlemler */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              Hızlı İşlemler
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
              <a href="/orders">
                <ShoppingCart className="w-5 h-5 text-blue-500" />
                <span className="text-sm">Siparişler</span>
              </a>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
              <a href="/products">
                <Package className="w-5 h-5 text-emerald-500" />
                <span className="text-sm">Ürünler</span>
              </a>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
              <a href="/auto-sync">
                <Zap className="w-5 h-5 text-purple-500" />
                <span className="text-sm">Otomatik Sync</span>
              </a>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
              <a href="/incoterm">
                <Globe className="w-5 h-5 text-indigo-500" />
                <span className="text-sm">Incoterm</span>
              </a>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
              <a href="/stores">
                <Store className="w-5 h-5 text-pink-500" />
                <span className="text-sm">Mağazalar</span>
              </a>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
              <a href="/logs">
                <BarChart3 className="w-5 h-5 text-orange-500" />
                <span className="text-sm">Loglar</span>
              </a>
            </Button>
          </CardContent>
        </Card>

        {/* Son İşlemler */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-500" />
                Son İşlemler
              </CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <a href="/logs">Tümünü Gör</a>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.recent_logs?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_logs.slice(0, 5).map((log, index) => (
                  <div key={index} className="flex items-start gap-3 text-sm p-2 hover:bg-slate-50 rounded-lg transition-colors">
                    {getLogIcon(log.log_type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-700 truncate">{log.message}</p>
                      <p className="text-xs text-slate-400">
                        {new Date(log.created_at).toLocaleString("tr-TR")}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {log.module}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">Henüz log kaydı bulunmuyor</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Senkronizasyon Özeti */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            Senkronizasyon Özeti
          </CardTitle>
          <CardDescription>Son 24 saat içindeki işlemler</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-xl">
              <p className="text-3xl font-bold text-blue-600">{stats?.sync_summary?.products_synced || 0}</p>
              <p className="text-sm text-blue-600 mt-1">Ürün Sync</p>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-xl">
              <p className="text-3xl font-bold text-emerald-600">{stats?.sync_summary?.stock_updated || 0}</p>
              <p className="text-sm text-emerald-600 mt-1">Stok Güncelleme</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-xl">
              <p className="text-3xl font-bold text-purple-600">{stats?.sync_summary?.price_updated || 0}</p>
              <p className="text-sm text-purple-600 mt-1">Fiyat Güncelleme</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-xl">
              <p className="text-3xl font-bold text-amber-600">{stats?.sync_summary?.orders_transferred || 0}</p>
              <p className="text-sm text-amber-600 mt-1">Sipariş Aktarım</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
